import React, { useState, useEffect, useCallback, useRef, DragEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { API_DOCS } from './DettaglioFinanziamento.api';
import TrendRealeFinanziamento from './TrendRealeFinanziamento';
import {
  Cadenza, FinanziamentoLite, RataDisponibile, FormState, FormMovimento,
  emptyForm, derivaPeriodoCompetenza, validaFormMovimento, buildPayloadMovimento,
  prefillFormDaRata,
} from '../movimenti-finanziamenti/movimentoForm';

// ── Icone ─────────────────────────────────────────────────────────────────────

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ContabilizzaIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 12h.01M18 12h.01" />
  </svg>
);

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface Rata {
  id: number;
  numeroRata: number;
  dataScadenza: string;
  importoRata: number;
  quotaCapitale: number;
  quotaInteressi: number;
  speseIncasso: number;
  debitoResiduo: number;
  isPagata: boolean;
  isPreammortamento: boolean;
  isManuale: boolean;
  notaManuale: string | null;
}

interface StoricoTasso {
  id: number;
  tassoVecchio: number;
  tassoNuovo: number;
  dataDecorrenza: string;
  motivazione: string | null;
  utenteNome: string | null;
}

interface Allegato {
  id: number;
  nomeFile: string;
  mimeType: string;
  dimensioneBytes: number;
  createdAt: string;
  uploadedBy: { id: number; nome: string };
}

interface Riepilogo {
  ratePagate: number;
  rateFuture: number;
  capitalePagato: number;
  interessiPagati: number;
  debitoResiduo: number;
  tassoAttuale: number;
}

interface FinanziamentoDettaglio {
  id: number;
  societaId: number;
  bancaId: number | null;
  nomeBanca: string;
  descrizione: string;
  numeroFinanziamento: string | null;
  finalita: string | null;
  garanziaMcc: boolean;
  garanziePrestate: string | null;
  importoOriginale: number;
  dataErogazione: string;
  durataAnni: number;
  tipoAmmortamento: string;
  tipoTasso: string;
  tassoIniziale: number;
  cadenzaRata: string;
  dataPrimaRata: string;
  haPreammortamento: boolean;
  durataPreammortamentoMesi: number | null;
  tipoAsset: string | null;
  descrizioneAsset: string | null;
  valoreStimatoAsset: number | null;
  stato: string;
  note: string | null;
  costoIstruttoria: number | null;
  costoPratica: number | null;
  costoFissoIniziale: number | null;
  tassoEstinzioneAnticipata: number | null;
  speseIncassoRata: number | null;
  storiciTasso: StoricoTasso[];
  rate: Rata[];
  allegati: Allegato[];
  riepilogo: Riepilogo;
}

interface Banca { id: number; nome: string; }

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtData = (d: string) =>
  new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

const fmtBytes = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIconLabel = (mime: string, name: string): string => {
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (mime.includes('spreadsheet') || mime.includes('excel') || /\.(xls|xlsx)$/i.test(name)) return 'XLS';
  if (mime.includes('word') || /\.(doc|docx)$/i.test(name)) return 'DOC';
  if (mime.includes('image')) return 'IMG';
  return 'FILE';
};

const STATO_LABELS: Record<string, string> = {
  ATTIVO:  'Attivo',
  ESTINTO: 'Estinto',
  SOSPESO: 'Sospeso',
};
const STATO_BADGE_CLASS: Record<string, string> = {
  ATTIVO:  'bop-badge bop-badge-success',
  ESTINTO: 'bop-badge bop-badge-neutral',
  SOSPESO: 'bop-badge bop-badge-warning',
};
const TIPO_TASSO_BADGE_CLASS: Record<string, string> = {
  FISSO:     'bop-badge bop-badge-info',
  VARIABILE: 'bop-badge bop-badge-warning',
};
const TIPO_ASSET_LABELS: Record<string, string> = {
  IMMOBILE_RESIDENZIALE: 'Immobile Residenziale',
  IMMOBILE_COMMERCIALE:  'Immobile Commerciale',
  IMMOBILE_INDUSTRIALE:  'Immobile Industriale',
  MACCHINARIO:           'Macchinario',
  VEICOLO:               'Veicolo',
  ATTREZZATURA:          'Attrezzatura',
  ALTRO:                 'Altro',
};
const TIPO_AMM_LABELS: Record<string, string> = {
  FRANCESE:  'Francese',
  ITALIANO:  'Italiano',
  AMERICANO: 'Americano',
  BULLET:    'Bullet',
};
const CADENZA_LABELS: Record<string, string> = {
  MENSILE:      'Mensile',
  TRIMESTRALE:  'Trimestrale',
  SEMESTRALE:   'Semestrale',
  ANNUALE:      'Annuale',
};

// ── Componente ────────────────────────────────────────────────────────────────

export default function DettaglioFinanziamento() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermesso();

  const [finanziamento, setFinanziamento] = useState<FinanziamentoDettaglio | null>(null);
  const [banche, setBanche] = useState<Banca[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'generale' | 'piano' | 'rate' | 'trend-reale' | 'allegati'>('generale');

  // ── Form Tab 1 ─────────────────────────────────────────────────────────────
  const [fBancaId,          setFBancaId]          = useState('');
  const [fNumeroFin,        setFNumeroFin]        = useState('');
  const [fDescriz,          setFDescriz]          = useState('');
  const [fFinalita,         setFFinalita]         = useState('');
  const [fStato,            setFStato]            = useState('');
  const [fTipoAsset,        setFTipoAsset]        = useState('');
  const [fDescrizAsset,     setFDescrizAsset]     = useState('');
  const [fValoreAsset,      setFValoreAsset]      = useState('');
  const [fGaranziaMcc,      setFGaranziaMcc]      = useState(false);
  const [fGaranziePrestate, setFGaranziePrestate] = useState('');
  const [fCostoIstr,        setFCostoIstr]        = useState('');
  const [fCostoPratica,     setFCostoPratica]     = useState('');
  const [fCostoFisso,       setFCostoFisso]       = useState('');
  const [fTassoEstinz,      setFTassoEstinz]      = useState('');
  const [fSpeseIncasso,     setFSpeseIncasso]     = useState('');
  const [fNote,             setFNote]             = useState('');
  const [isSaving,          setIsSaving]          = useState(false);

  // ── Form Tab 2 — tasso ─────────────────────────────────────────────────────
  const [showTassoForm,   setShowTassoForm]   = useState(false);
  const [fNuovoTasso,     setFNuovoTasso]     = useState('');
  const [fDataDecorrenza, setFDataDecorrenza] = useState(new Date().toISOString().split('T')[0]);
  const [fMotivazione,    setFMotivazione]    = useState('');
  const [isSavingTasso,   setIsSavingTasso]   = useState(false);

  // ── Filtri Tab 3 — rate ───────────────────────────────────────────────────
  const [filterRataStato, setFilterRataStato] = useState<'TUTTE' | 'PAGATE' | 'FUTURE'>('TUTTE');
  const [filterRataAnno,  setFilterRataAnno]  = useState('');
  const [editingRataId,   setEditingRataId]   = useState<number | null>(null);
  const [fRataImporto,    setFRataImporto]    = useState('');
  const [fRataNota,       setFRataNota]       = useState('');
  const [isSavingRata,    setIsSavingRata]    = useState(false);

  // ── Contabilizza rata — modale movimento finanziamento ──────────────────────
  const [contabOpen,        setContabOpen]        = useState(false);
  const [contabForm,        setContabForm]        = useState<FormState>(() => emptyForm(0));
  const [contabError,       setContabError]       = useState('');
  const [contabSaving,      setContabSaving]      = useState(false);
  const [contabRate,        setContabRate]        = useState<RataDisponibile[]>([]);
  const [contabManuPeriodo, setContabManuPeriodo] = useState(false);

  // ── Tab 4 — allegati ──────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAllegato, setIsUploadingAllegato] = useState(false);
  const [isDragging,          setIsDragging]          = useState(false);

  // Blocco cambio società mentre si è nella scheda
  useBloccoRecordContesto({
    recordSocietaId: finanziamento?.societaId,
    label: finanziamento
      ? `Finanziamento ${finanziamento.descrizione || finanziamento.numeroFinanziamento || `#${finanziamento.id}`}`
      : 'Finanziamento',
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // @api GET /api/finanziamenti/:id -> { finanziamento: {...} }
      const data = await apiFetch<{ finanziamento: FinanziamentoDettaglio }>(`/api/finanziamenti/${id}`);
      setFinanziamento(data.finanziamento);
    } catch (err: any) {
      showToast(err.message || 'Errore nel caricamento', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const societaId = finanziamento?.societaId;
  useEffect(() => {
    if (!societaId) return;
    // @api GET /api/banche?societaId= -> [{ id, nome }]
    apiFetch<Banca[]>(`/api/banche?societaId=${societaId}`)
      .then(d => setBanche(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [societaId]);

  // ── Init form da finanziamento ────────────────────────────────────────────
  useEffect(() => {
    if (!finanziamento) return;
    setFBancaId(finanziamento.bancaId != null ? String(finanziamento.bancaId) : '');
    setFNumeroFin(finanziamento.numeroFinanziamento || '');
    setFDescriz(finanziamento.descrizione || '');
    setFFinalita(finanziamento.finalita || '');
    setFStato(finanziamento.stato || '');
    setFTipoAsset(finanziamento.tipoAsset || '');
    setFDescrizAsset(finanziamento.descrizioneAsset || '');
    setFValoreAsset(finanziamento.valoreStimatoAsset != null ? String(finanziamento.valoreStimatoAsset) : '');
    setFGaranziaMcc(finanziamento.garanziaMcc || false);
    setFGaranziePrestate(finanziamento.garanziePrestate || '');
    setFCostoIstr(finanziamento.costoIstruttoria != null ? String(finanziamento.costoIstruttoria) : '');
    setFCostoPratica(finanziamento.costoPratica != null ? String(finanziamento.costoPratica) : '');
    setFCostoFisso(finanziamento.costoFissoIniziale != null ? String(finanziamento.costoFissoIniziale) : '');
    setFTassoEstinz(finanziamento.tassoEstinzioneAnticipata != null ? String(finanziamento.tassoEstinzioneAnticipata) : '');
    setFSpeseIncasso(finanziamento.speseIncassoRata != null ? String(finanziamento.speseIncassoRata) : '');
    setFNote(finanziamento.note || '');
  }, [finanziamento]);

  // ── Valori derivati ───────────────────────────────────────────────────────
  const rateTotali = finanziamento
    ? finanziamento.riepilogo.ratePagate + finanziamento.riepilogo.rateFuture
    : 0;

  const prossimaRata = finanziamento?.rate.find(r => !r.isPagata) ?? null;

  const totaleInteressiPiano = useMemo(() => {
    if (!finanziamento) return 0;
    return finanziamento.rate.reduce((sum, r) => sum + Number(r.quotaInteressi), 0);
  }, [finanziamento]);

  const costoTotaleStimatoKpi = finanziamento
    ? (finanziamento.costoIstruttoria || 0)
      + (finanziamento.costoPratica || 0)
      + (finanziamento.costoFissoIniziale || 0)
      + (finanziamento.speseIncassoRata || 0) * rateTotali
      + totaleInteressiPiano
    : 0;

  const costoLiveTab1 = useMemo(() => {
    if (!finanziamento) return 0;
    return (parseFloat(fCostoIstr) || 0)
      + (parseFloat(fCostoPratica) || 0)
      + (parseFloat(fCostoFisso) || 0)
      + (parseFloat(fSpeseIncasso) || 0) * rateTotali
      + finanziamento.riepilogo.interessiPagati;
  }, [fCostoIstr, fCostoPratica, fCostoFisso, fSpeseIncasso, rateTotali, finanziamento]);

  const anniRate = useMemo(() => {
    if (!finanziamento) return [];
    const set = new Set<number>();
    finanziamento.rate.forEach(r => set.add(new Date(r.dataScadenza).getFullYear()));
    return Array.from(set).sort();
  }, [finanziamento]);

  const filteredRate = useMemo(() => {
    if (!finanziamento) return [];
    let r = finanziamento.rate;
    if (filterRataStato === 'PAGATE') r = r.filter(x => x.isPagata);
    if (filterRataStato === 'FUTURE') r = r.filter(x => !x.isPagata);
    if (filterRataAnno) r = r.filter(x => new Date(x.dataScadenza).getFullYear() === parseInt(filterRataAnno));
    return r;
  }, [finanziamento, filterRataStato, filterRataAnno]);

  const totaliRate = useMemo(() => ({
    capitale:     filteredRate.reduce((s, r) => s + Number(r.quotaCapitale), 0),
    interessi:    filteredRate.reduce((s, r) => s + Number(r.quotaInteressi), 0),
    speseIncasso: filteredRate.reduce((s, r) => s + Number(r.speseIncasso || 0), 0),
    importoRata:  filteredRate.reduce((s, r) => s + Number(r.importoRata), 0),
  }), [filteredRate]);

  // ── Handlers salvataggio ──────────────────────────────────────────────────
  const doSave = async (overrides?: Record<string, any>) => {
    setIsSaving(true);
    try {
      const payload = {
        bancaId:                   fBancaId ? parseInt(fBancaId) : null,
        descrizione:               fDescriz,
        note:                      fNote || null,
        stato:                     fStato,
        tipoAsset:                 fTipoAsset || null,
        descrizioneAsset:          fDescrizAsset || null,
        valoreStimatoAsset:        fValoreAsset ? parseFloat(fValoreAsset) : null,
        numeroFinanziamento:       fNumeroFin || null,
        finalita:                  fFinalita || null,
        garanziaMcc:               fGaranziaMcc,
        garanziePrestate:          fGaranziePrestate || null,
        costoIstruttoria:          fCostoIstr ? parseFloat(fCostoIstr) : null,
        costoPratica:              fCostoPratica ? parseFloat(fCostoPratica) : null,
        costoFissoIniziale:        fCostoFisso ? parseFloat(fCostoFisso) : null,
        tassoEstinzioneAnticipata: fTassoEstinz ? parseFloat(fTassoEstinz) : null,
        speseIncassoRata:          fSpeseIncasso ? parseFloat(fSpeseIncasso) : null,
        ...overrides,
      };
      // @api PUT /api/finanziamenti/:id {...} -> { finanziamento: {...} }
      await apiFetch(`/api/finanziamenti/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      showToast('Modifiche salvate', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Errore nel salvataggio', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave          = () => doSave();
  const handleSegnaEstinto  = () => doSave({ stato: 'ESTINTO' });

  const handleSaveTasso = async () => {
    if (!fNuovoTasso || !fDataDecorrenza) {
      showToast('Inserisci tasso e data decorrenza', 'error');
      return;
    }
    setIsSavingTasso(true);
    try {
      // @api PUT /api/finanziamenti/:id/tasso { nuovoTasso, dataDecorrenza, motivazione } -> { ok: true }
      await apiFetch(`/api/finanziamenti/${id}/tasso`, {
        method: 'PUT',
        body: JSON.stringify({
          nuovoTasso:      parseFloat(fNuovoTasso),
          dataDecorrenza:  fDataDecorrenza,
          motivazione:     fMotivazione || null,
        }),
      });
      showToast('Tasso aggiornato e rate ricalcolate', 'success');
      setShowTassoForm(false);
      setFNuovoTasso('');
      setFMotivazione('');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Errore nel salvataggio tasso', 'error');
    } finally {
      setIsSavingTasso(false);
    }
  };

  const handleSaveRata = async () => {
    if (!editingRataId || !fRataImporto) return;
    setIsSavingRata(true);
    try {
      // @api PUT /api/finanziamenti/:id/rate/:rataId { importoRata, notaManuale } -> { ok: true }
      await apiFetch(`/api/finanziamenti/${id}/rate/${editingRataId}`, {
        method: 'PUT',
        body: JSON.stringify({
          importoRata:  parseFloat(fRataImporto),
          notaManuale:  fRataNota || null,
        }),
      });
      showToast('Rata aggiornata', 'success');
      setEditingRataId(null);
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Errore nel salvataggio rata', 'error');
    } finally {
      setIsSavingRata(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    setIsUploadingAllegato(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // @api POST /api/finanziamenti/:id/allegati FormData -> { ok: true, allegato: {...} }
      await apiFetch(`/api/finanziamenti/${id}/allegati`, { method: 'POST', body: formData });
      showToast('Allegato caricato', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Errore nel caricamento allegato', 'error');
    } finally {
      setIsUploadingAllegato(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAllegato = async (allegatoId: number) => {
    if (!window.confirm('Eliminare l\'allegato?')) return;
    try {
      // @api DELETE /api/finanziamenti/:id/allegati/:allegatoId -> { ok: true }
      await apiFetch(`/api/finanziamenti/${id}/allegati/${allegatoId}`, { method: 'DELETE' });
      showToast('Allegato eliminato', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Errore', 'error');
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUploadFile(file);
  };

  const openEditRata = (rata: Rata) => {
    setEditingRataId(rata.id);
    setFRataImporto(String(rata.importoRata));
    setFRataNota(rata.notaManuale || '');
  };

  // ── Contabilizza rata ──────────────────────────────────────────────────────
  const openContabilizza = async (rata: Rata) => {
    if (!finanziamento) return;
    setContabError('');
    setContabManuPeriodo(false);
    setContabRate([]);
    setContabForm(prefillFormDaRata({
      societaId: finanziamento.societaId,
      finanziamentoId: finanziamento.id,
      cadenza: finanziamento.cadenzaRata as Cadenza,
      rata: {
        id: rata.id,
        dataScadenza: rata.dataScadenza,
        quotaCapitale: rata.quotaCapitale,
        quotaInteressi: rata.quotaInteressi,
        speseIncasso: rata.speseIncasso,
      },
    }));
    setContabOpen(true);
    try {
      const params = new URLSearchParams({
        finanziamentoId: String(finanziamento.id),
        includiId: String(rata.id),
      });
      // @api GET /api/movimenti-finanziamento/rate-disponibili?finanziamentoId=&includiId= -> { success: true, data: [{ id, numeroRata, dataScadenza, importoRata, quotaCapitale, quotaInteressi }] }
      const res = await apiFetch<{ success: boolean; data: RataDisponibile[] }>(
        `/api/movimenti-finanziamento/rate-disponibili?${params}`,
      );
      setContabRate(res.data ?? []);
    } catch (err: any) {
      setContabError(err.message || 'Errore nel caricamento rate disponibili');
    }
  };

  const ricalcolaPeriodoContab = (next: FormState) => {
    if (!contabManuPeriodo && next.dataPagamento) {
      const p = derivaPeriodoCompetenza(next.dataPagamento, next.cadenzaCompetenza, next.modalitaCompetenza);
      next.periodoCompetenzaDa = p.da;
      next.periodoCompetenzaA = p.a;
    }
    return next;
  };

  const salvaContabilizzazione = async () => {
    const verr = validaFormMovimento(contabForm);
    if (verr) { setContabError(verr); return; }
    setContabSaving(true);
    setContabError('');
    try {
      const payload = buildPayloadMovimento(contabForm);
      // @api POST /api/movimenti-finanziamento { finanziamentoId, dataPagamento, quotaCapitale, quotaInteressi, spese, cadenzaCompetenza, modalitaCompetenza, periodoCompetenzaDa, periodoCompetenzaA, rataTeoricaId, note } -> { success: true, data: { id } }
      await apiFetch('/api/movimenti-finanziamento', { method: 'POST', body: JSON.stringify(payload) });
      showToast('Rata contabilizzata', 'success');
      setContabOpen(false);
      fetchData();
    } catch (err: any) {
      setContabError(err.message || 'Errore nella contabilizzazione');
    } finally {
      setContabSaving(false);
    }
  };

  const contabFinList: FinanziamentoLite[] = finanziamento
    ? [{
        id: finanziamento.id,
        societaId: finanziamento.societaId,
        descrizione: finanziamento.descrizione,
        nomeBanca: finanziamento.nomeBanca,
        cadenzaRata: finanziamento.cadenzaRata as Cadenza,
        numeroFinanziamento: finanziamento.numeroFinanziamento ?? undefined,
      }]
    : [];

  // ── Loading guards ────────────────────────────────────────────────────────
  if (isLoading) return (
    <Layout title="Dettaglio Finanziamento">
      <div className="bop-page__loading">Caricamento...</div>
    </Layout>
  );
  if (!finanziamento) return (
    <Layout title="Finanziamento">
      <div className="bop-empty-state">Finanziamento non trovato</div>
    </Layout>
  );

  const { riepilogo } = finanziamento;
  const debitoResiduoPct = finanziamento.importoOriginale > 0
    ? Math.round((riepilogo.debitoResiduo / finanziamento.importoOriginale) * 100)
    : 0;
  const avanzamentoPct = rateTotali > 0
    ? Math.round((riepilogo.ratePagate / rateTotali) * 100)
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout title={finanziamento.descrizione}>
      <div className="bop-finanz-det__page">

        {/* ── Back link ── */}
        <BopBackLink to="/amministrazione/finanziamenti" label="Torna ai finanziamenti" />

        {/* ── Header ── */}
        <div className="bop-finanz-det__header">
          <div className="bop-finanz-det__header-left">
            <h1 className="bop-finanz-det__title">{finanziamento.descrizione}</h1>
            <div className="bop-finanz-det__subtitle">
              <span className="bop-finanz-det__subtitle-banca">
                {finanziamento.nomeBanca}
                {finanziamento.numeroFinanziamento && ` — ${finanziamento.numeroFinanziamento}`}
              </span>
              <span className={STATO_BADGE_CLASS[finanziamento.stato] || 'bop-badge bop-badge-neutral'}>
                {STATO_LABELS[finanziamento.stato] || finanziamento.stato}
              </span>
              <span className={TIPO_TASSO_BADGE_CLASS[finanziamento.tipoTasso] || 'bop-badge bop-badge-neutral'}>
                {finanziamento.tipoTasso}
              </span>
            </div>
          </div>
          <div className="bop-finanz-det__header-actions">
            {finanziamento.stato !== 'ESTINTO' && can('amministrazione.finanziamenti', 'delete') && (
              <button
                className="bop-btn-secondary"
                onClick={handleSegnaEstinto}
                disabled={isSaving}
              >
                Segna estinto
              </button>
            )}
            {can('amministrazione.finanziamenti', 'edit') && (
              <button
                className="bop-btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
              </button>
            )}
          </div>
        </div>

        {/* ── KPI bar ── */}
        <div className="bop-finanz-det__kpi-bar">
          <div className="bop-card bop-finanz-det__kpi-item">
            <div className="bop-finanz__kpi-label">Importo originale</div>
            <div className="bop-finanz__kpi-value">{formatCurrency(finanziamento.importoOriginale)}</div>
            <div className="bop-finanz__kpi-date">Erogato il {fmtData(finanziamento.dataErogazione)}</div>
          </div>

          <div className="bop-card bop-finanz-det__kpi-item">
            <div className="bop-finanz__kpi-label">Debito residuo</div>
            <div className={`bop-finanz__kpi-value ${debitoResiduoPct > 50 ? 'bop-finanz__kpi-value--danger' : 'bop-finanz__kpi-value--success'}`}>
              {formatCurrency(riepilogo.debitoResiduo)}
            </div>
            <div className="bop-finanz__kpi-sub">{debitoResiduoPct}% dell'originale</div>
          </div>

          <div className="bop-card bop-finanz-det__kpi-item">
            <div className="bop-finanz__kpi-label">Prossima rata</div>
            {prossimaRata ? (
              <>
                <div className="bop-finanz__kpi-value--sm">{formatCurrency(prossimaRata.importoRata)}</div>
                <div className="bop-finanz__kpi-date">{fmtData(prossimaRata.dataScadenza)}</div>
              </>
            ) : (
              <div className="bop-finanz__kpi-empty">Nessuna rata futura</div>
            )}
          </div>

          <div className="bop-card bop-finanz-det__kpi-item">
            <div className="bop-finanz__kpi-label">Avanzamento</div>
            <div className="bop-finanz__kpi-value--sm">{riepilogo.ratePagate} / {rateTotali} rate</div>
            <div className="bop-finanz__progress-track">
              <div
                className={`bop-finanz__progress-bar ${avanzamentoPct < 33 ? 'bop-finanz__progress-bar--danger' : avanzamentoPct < 66 ? 'bop-finanz__progress-bar--warning' : 'bop-finanz__progress-bar--success'}`}
                style={{ width: `${avanzamentoPct}%` }}
              />
            </div>
            <div className="bop-finanz__kpi-sub">{avanzamentoPct}%</div>
          </div>

          <div className="bop-card bop-finanz-det__kpi-item">
            <div className="bop-finanz__kpi-label">Costo totale stimato</div>
            <div className="bop-finanz__kpi-value--sm">{formatCurrency(costoTotaleStimatoKpi)}</div>
            <div className="bop-finanz__kpi-sub">Costi + interessi totali piano</div>
          </div>
        </div>

        {/* ── Navigazione tab ── */}
        <div className="bop-tabs">
          {(['generale', 'piano', 'rate', 'trend-reale', 'allegati'] as const).map(tab => (
            <button
              key={tab}
              className={`bop-tab${activeTab === tab ? ' active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'generale' ? 'Dati generali'
                : tab === 'piano' ? 'Piano e tasso'
                : tab === 'rate' ? 'Rate'
                : tab === 'trend-reale' ? 'Trend reale'
                : 'Allegati'}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 1 — Dati generali
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'generale' && (
          <div className="bop-finanz-det__tab-content">

            {/* Card Intestazione */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Intestazione</div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Banca</label>
                  <select
                    className="bop-select"
                    value={fBancaId}
                    onChange={e => setFBancaId(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  >
                    <option value="">Seleziona banca</option>
                    {banche.map(b => (
                      <option key={b.id} value={String(b.id)}>{b.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">N. contratto / finanziamento</label>
                  <input
                    className="bop-input"
                    value={fNumeroFin}
                    onChange={e => setFNumeroFin(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Descrizione *</label>
                  <input
                    className="bop-input"
                    value={fDescriz}
                    onChange={e => setFDescriz(e.target.value)}
                    required
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Finalità</label>
                  <input
                    className="bop-input"
                    value={fFinalita}
                    onChange={e => setFFinalita(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Stato</label>
                  <select
                    className="bop-select"
                    value={fStato}
                    onChange={e => setFStato(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  >
                    <option value="ATTIVO">Attivo</option>
                    <option value="ESTINTO">Estinto</option>
                    <option value="SOSPESO">Sospeso</option>
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Tipo asset</label>
                  <select
                    className="bop-select"
                    value={fTipoAsset}
                    onChange={e => setFTipoAsset(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  >
                    <option value="">Nessuno</option>
                    {Object.entries(TIPO_ASSET_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              {fTipoAsset && (
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Descrizione asset</label>
                    <input
                      className="bop-input"
                      value={fDescrizAsset}
                      onChange={e => setFDescrizAsset(e.target.value)}
                      disabled={!can('amministrazione.finanziamenti', 'edit')}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Valore stimato asset (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bop-input"
                      value={fValoreAsset}
                      onChange={e => setFValoreAsset(e.target.value)}
                      disabled={!can('amministrazione.finanziamenti', 'edit')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Card Garanzie */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Garanzie</div>
              <div className="bop-finanz__check-row bop-finanz__mb-2">
                <input
                  type="checkbox"
                  id="garanziaMcc"
                  checked={fGaranziaMcc}
                  onChange={e => setFGaranziaMcc(e.target.checked)}
                  disabled={!can('amministrazione.finanziamenti', 'edit')}
                />
                <label htmlFor="garanziaMcc" className="bop-finanz__check-label--md">
                  Garanzia MCC (Mediocredito Centrale)
                </label>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Garanzie prestate</label>
                <textarea
                  className="bop-input bop-finanz-det__textarea"
                  rows={3}
                  value={fGaranziePrestate}
                  onChange={e => setFGaranziePrestate(e.target.value)}
                  disabled={!can('amministrazione.finanziamenti', 'edit')}
                  placeholder="Descrivere le garanzie prestate..."
                />
              </div>
            </div>

            {/* Card Costi e commissioni */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Costi e commissioni</div>
              <div className="bop-finanz-det__cost-grid">
                <div className="bop-form-group">
                  <label className="bop-label">Costo istruttoria (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="bop-input"
                    value={fCostoIstr}
                    onChange={e => setFCostoIstr(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Costo pratica (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="bop-input"
                    value={fCostoPratica}
                    onChange={e => setFCostoPratica(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Costo fisso iniziale (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="bop-input"
                    value={fCostoFisso}
                    onChange={e => setFCostoFisso(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Tasso estinzione anticipata (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="bop-input"
                    value={fTassoEstinz}
                    onChange={e => setFTassoEstinz(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
              </div>
              <div className="bop-finanz-det__spese-row">
                <div className="bop-form-group bop-finanz-det__spese-input">
                  <label className="bop-label">Spese incasso rata (€/rata)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="bop-input"
                    value={fSpeseIncasso}
                    onChange={e => setFSpeseIncasso(e.target.value)}
                    disabled={!can('amministrazione.finanziamenti', 'edit')}
                  />
                </div>
              </div>
              <div className="bop-finanz__info-box bop-finanz-det__cost-summary">
                <div className="bop-finanz-det__cost-summary-row">
                  <span className="bop-finanz-det__cost-summary-label">Costi up-front:</span>
                  <span className="bop-finanz-det__cost-summary-value">
                    {formatCurrency((parseFloat(fCostoIstr) || 0) + (parseFloat(fCostoPratica) || 0) + (parseFloat(fCostoFisso) || 0))}
                  </span>
                </div>
                <div className="bop-finanz-det__cost-summary-row">
                  <span className="bop-finanz-det__cost-summary-label">Spese incasso totali:</span>
                  <span className="bop-finanz-det__cost-summary-value">
                    {formatCurrency((parseFloat(fSpeseIncasso) || 0) * rateTotali)}
                    <span className="bop-text-muted"> ({rateTotali} rate)</span>
                  </span>
                </div>
                <div className="bop-finanz-det__cost-summary-row bop-finanz-det__cost-summary-row--total">
                  <span className="bop-finanz-det__cost-summary-label">Costo complessivo stimato:</span>
                  <span className="bop-finanz-det__cost-summary-total">
                    {formatCurrency(costoLiveTab1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Card Note */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Note interne</div>
              <div className="bop-form-group">
                <textarea
                  className="bop-input bop-finanz-det__textarea"
                  rows={4}
                  value={fNote}
                  onChange={e => setFNote(e.target.value)}
                  disabled={!can('amministrazione.finanziamenti', 'edit')}
                  placeholder="Note interne sul finanziamento..."
                />
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 2 — Piano e tasso
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'piano' && (
          <div className="bop-finanz-det__tab-content">

            {/* Card parametri fissi (sola lettura) */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Parametri fissi (sola lettura)</div>
              <div className="bop-finanz-det__readonly-grid">
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Importo originale</div>
                  <div className="bop-finanz-det__readonly-value">{formatCurrency(finanziamento.importoOriginale)}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Durata</div>
                  <div className="bop-finanz-det__readonly-value">{finanziamento.durataAnni} anni</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Data erogazione</div>
                  <div className="bop-finanz-det__readonly-value">{fmtData(finanziamento.dataErogazione)}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Data prima rata</div>
                  <div className="bop-finanz-det__readonly-value">{fmtData(finanziamento.dataPrimaRata)}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Tipo ammortamento</div>
                  <div className="bop-finanz-det__readonly-value">{TIPO_AMM_LABELS[finanziamento.tipoAmmortamento] || finanziamento.tipoAmmortamento}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Cadenza rata</div>
                  <div className="bop-finanz-det__readonly-value">{CADENZA_LABELS[finanziamento.cadenzaRata] || finanziamento.cadenzaRata}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Tipo tasso</div>
                  <div className="bop-finanz-det__readonly-value">{finanziamento.tipoTasso}</div>
                </div>
                <div className="bop-finanz-det__readonly-field">
                  <div className="bop-finanz-det__readonly-label">Tasso iniziale</div>
                  <div className="bop-finanz-det__readonly-value">{formatNumber(Number(finanziamento.tassoIniziale))}%</div>
                </div>
                {finanziamento.haPreammortamento && finanziamento.durataPreammortamentoMesi && (
                  <div className="bop-finanz-det__readonly-field">
                    <div className="bop-finanz-det__readonly-label">Preammortamento</div>
                    <div className="bop-finanz-det__readonly-value">{finanziamento.durataPreammortamentoMesi} mesi</div>
                  </div>
                )}
              </div>
            </div>

            {/* Banner tasso attuale */}
            {finanziamento.tipoTasso === 'VARIABILE' ? (
              <div className="bop-finanz-det__banner bop-finanz-det__banner--warning">
                <div className="bop-finanz-det__banner-text">
                  <strong>Tasso attuale: {formatNumber(Number(riepilogo.tassoAttuale))}%</strong>
                  {finanziamento.storiciTasso.length > 0 && (
                    <span className="bop-finanz-det__banner-since">
                      {' '}— decorrenza {fmtData(finanziamento.storiciTasso[finanziamento.storiciTasso.length - 1].dataDecorrenza)}
                    </span>
                  )}
                </div>
                {can('amministrazione.finanziamenti', 'edit') && !showTassoForm && (
                  <button
                    className="bop-btn-secondary bop-finanz-det__banner-btn"
                    onClick={() => setShowTassoForm(true)}
                  >
                    + Nuova variazione tasso
                  </button>
                )}
              </div>
            ) : (
              <div className="bop-finanz-det__banner bop-finanz-det__banner--info">
                <div className="bop-finanz-det__banner-text">
                  Tasso fisso: <strong>{formatNumber(Number(finanziamento.tassoIniziale))}%</strong>
                </div>
              </div>
            )}

            {/* Form nuova variazione tasso */}
            {showTassoForm && finanziamento.tipoTasso === 'VARIABILE' && can('amministrazione.finanziamenti', 'edit') && (
              <div className="bop-card bop-finanz-det__tasso-form">
                <div className="bop-finanz__section-title--sm">Nuova variazione tasso</div>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Nuovo tasso (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bop-input"
                      value={fNuovoTasso}
                      onChange={e => setFNuovoTasso(e.target.value)}
                      placeholder="Es. 3.25"
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Data decorrenza</label>
                    <input
                      type="date"
                      className="bop-input"
                      value={fDataDecorrenza}
                      onChange={e => setFDataDecorrenza(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Motivazione</label>
                    <input
                      className="bop-input"
                      value={fMotivazione}
                      onChange={e => setFMotivazione(e.target.value)}
                      placeholder="Es. rinegoziazione contrattuale"
                    />
                  </div>
                </div>
                <div className="bop-finanz__modal-footer">
                  <button className="bop-btn-secondary" onClick={() => setShowTassoForm(false)}>
                    Annulla
                  </button>
                  <button className="bop-btn-primary" onClick={handleSaveTasso} disabled={isSavingTasso}>
                    {isSavingTasso ? 'Salvataggio...' : 'Salva e ricalcola rate future'}
                  </button>
                </div>
              </div>
            )}

            {/* Card storico variazioni tasso */}
            <div className="bop-card">
              <div className="bop-finanz__section-title">Storico variazioni tasso</div>
              {finanziamento.storiciTasso.length === 0 ? (
                <div className="bop-empty-state">Nessuna variazione registrata</div>
              ) : (
                <div className="bop-finanz-det__storico-list">
                  {finanziamento.storiciTasso.map(s => (
                    <div key={s.id} className="bop-finanz-det__storico-row">
                      <div className="bop-finanz-det__storico-tasso">
                        <span className="bop-finanz-det__storico-old">{formatNumber(Number(s.tassoVecchio))}%</span>
                        <span className="bop-finanz-det__storico-arrow">→</span>
                        <span className="bop-finanz-det__storico-new">{formatNumber(Number(s.tassoNuovo))}%</span>
                      </div>
                      <div className="bop-finanz-det__storico-meta">
                        <span className="bop-text-muted">{fmtData(s.dataDecorrenza)}</span>
                        {s.motivazione && (
                          <span className="bop-finanz-det__storico-motivazione">{s.motivazione}</span>
                        )}
                        {s.utenteNome && (
                          <span className="bop-text-muted">— {s.utenteNome}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 3 — Rate
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'rate' && (
          <div className="bop-finanz-det__tab-content">

            {/* Banner info manuale */}
            <div className="bop-finanz__info-note bop-finanz-det__rate-banner">
              Le rate con il simbolo ✏ sono state modificate manualmente per allineamento al piano banca.
            </div>

            {/* Toolbar filtri */}
            <div className="bop-finanz-det__rate-toolbar">
              <div className="bop-finanz-det__rate-filters">
                {(['TUTTE', 'PAGATE', 'FUTURE'] as const).map(f => (
                  <button
                    key={f}
                    className={`bop-finanz-det__btn-filter${filterRataStato === f ? ' bop-finanz-det__btn-filter--active' : ''}`}
                    onClick={() => setFilterRataStato(f)}
                  >
                    {f === 'TUTTE' ? 'Tutte' : f === 'PAGATE' ? 'Pagate' : 'Future'}
                  </button>
                ))}
              </div>
              <select
                className="bop-select bop-finanz-det__anno-select"
                value={filterRataAnno}
                onChange={e => setFilterRataAnno(e.target.value)}
              >
                <option value="">Tutti gli anni</option>
                {anniRate.map(a => (
                  <option key={a} value={String(a)}>{a}</option>
                ))}
              </select>
              <button
                className="bop-btn-secondary"
                onClick={() => alert('Funzione in arrivo')}
              >
                CSV
              </button>
            </div>

            {/* Tabella rate */}
            <div className="bop-card">
              <div className="bop-finanz__table-scroll">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>N Data</th>
                      <th className="bop-text-right">Capitale</th>
                      <th className="bop-text-right">Interessi</th>
                      <th className="bop-text-right">Sp. incasso</th>
                      <th className="bop-text-right">Totale rata</th>
                      <th className="bop-text-right">Residuo</th>
                      <th className="bop-text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRate.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="bop-empty-state">Nessuna rata</div>
                        </td>
                      </tr>
                    ) : (
                      filteredRate.map(rata => {
                        const isNext = !rata.isPagata && prossimaRata?.id === rata.id;
                        let rowClass = '';
                        if (rata.isPagata) rowClass = 'bop-finanz__rata--pagata';
                        else if (isNext) rowClass = 'bop-finanz__rata--prossima';
                        return (
                          <React.Fragment key={rata.id}>
                            <tr className={rowClass}>
                              <td>
                                <div className="bop-finanz__cell-primary">
                                  #{rata.numeroRata}
                                  {rata.isManuale && (
                                    <span
                                      className="bop-finanz-det__manuale-icon"
                                      title={rata.notaManuale || 'Modificata manualmente'}
                                    >
                                      {' '}✏
                                    </span>
                                  )}
                                </div>
                                <div className="bop-finanz__cell-secondary">{fmtData(rata.dataScadenza)}</div>
                              </td>
                              <td className="bop-text-right bop-finanz__cell-sm">{formatCurrency(rata.quotaCapitale)}</td>
                              <td className="bop-text-right bop-finanz__cell-sm">{formatCurrency(rata.quotaInteressi)}</td>
                              <td className="bop-text-right bop-finanz__cell-sm">{formatCurrency(rata.speseIncasso || 0)}</td>
                              <td className="bop-text-right bop-finanz__cell-amount">{formatCurrency(rata.importoRata)}</td>
                              <td className="bop-text-right bop-finanz__cell-sm">{formatCurrency(rata.debitoResiduo)}</td>
                              <td className="bop-text-center">
                                {!rata.isPagata && can('amministrazione.finanziamenti', 'edit') && (
                                  <div className="bop-table__actions">
                                    <button
                                      className="bop-btn-icon"
                                      title="Contabilizza rata"
                                      onClick={() => openContabilizza(rata)}
                                    >
                                      <ContabilizzaIcon />
                                    </button>
                                    <button
                                      className="bop-btn-icon"
                                      title="Modifica rata"
                                      onClick={() =>
                                        editingRataId === rata.id
                                          ? setEditingRataId(null)
                                          : openEditRata(rata)
                                      }
                                    >
                                      <EditIcon />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>

                            {/* Pannello override inline */}
                            {editingRataId === rata.id && (
                              <tr>
                                <td colSpan={7}>
                                  <div className="bop-finanz-det__override-panel">
                                    <div className="bop-finanz-det__override-title">
                                      Modifica rata #{rata.numeroRata} — Totale attuale: {formatCurrency(rata.importoRata)}
                                    </div>
                                    <div className="bop-form-row">
                                      <div className="bop-form-group">
                                        <label className="bop-label">Importo rata (€)</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          className="bop-input"
                                          value={fRataImporto}
                                          onChange={e => setFRataImporto(e.target.value)}
                                        />
                                      </div>
                                      <div className="bop-form-group">
                                        <label className="bop-label">Nota manuale</label>
                                        <input
                                          className="bop-input"
                                          value={fRataNota}
                                          onChange={e => setFRataNota(e.target.value)}
                                          placeholder="Es. arrotondamento banca"
                                        />
                                      </div>
                                    </div>
                                    <div className="bop-finanz__modal-footer">
                                      <button className="bop-btn-secondary" onClick={() => setEditingRataId(null)}>
                                        Annulla
                                      </button>
                                      <button
                                        className="bop-btn-primary"
                                        onClick={handleSaveRata}
                                        disabled={isSavingRata}
                                      >
                                        {isSavingRata ? 'Salvataggio...' : 'Salva'}
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                    {filteredRate.length > 0 && (
                      <tr className="bop-finanz-det__totale-row">
                        <td><strong>Totale ({filteredRate.length} rate)</strong></td>
                        <td className="bop-text-right"><strong>{formatCurrency(totaliRate.capitale)}</strong></td>
                        <td className="bop-text-right"><strong>{formatCurrency(totaliRate.interessi)}</strong></td>
                        <td className="bop-text-right"><strong>{formatCurrency(totaliRate.speseIncasso)}</strong></td>
                        <td className="bop-text-right"><strong>{formatCurrency(totaliRate.importoRata)}</strong></td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB — Trend reale (basato sui movimenti di pagamento reali)
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'trend-reale' && finanziamento && (
          <div className="bop-finanz-det__tab-content">
            <TrendRealeFinanziamento
              finanziamentoId={finanziamento.id}
              rateTeoriche={finanziamento.rate || []}
            />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 4 — Allegati
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'allegati' && (
          <div className="bop-finanz-det__tab-content">

            {/* Drop zone upload */}
            {can('amministrazione.finanziamenti', 'edit') && (
              <div className="bop-card">
                <div
                  className={`bop-finanz-det__upload-zone${isDragging ? ' bop-finanz-det__upload-zone--drag' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="bop-sr-only"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadFile(file);
                    }}
                  />
                  {isUploadingAllegato ? (
                    <div className="bop-finanz-det__upload-spinner">Caricamento in corso...</div>
                  ) : (
                    <div className="bop-finanz-det__upload-prompt">
                      <div className="bop-finanz-det__upload-icon">📎</div>
                      <div>
                        Trascina qui i documenti o{' '}
                        <span className="bop-finanz-det__upload-link">seleziona file</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lista allegati */}
            <div className="bop-card">
              {finanziamento.allegati.length === 0 ? (
                <div className="bop-empty-state">Nessun allegato</div>
              ) : (
                <div className="bop-finanz-det__allegato-list">
                  {finanziamento.allegati.map(a => (
                    <div key={a.id} className="bop-finanz-det__allegato-item">
                      <div className="bop-finanz-det__allegato-icon">
                        <span className="bop-finanz-det__file-badge">
                          {fileIconLabel(a.mimeType, a.nomeFile)}
                        </span>
                      </div>
                      <div className="bop-finanz-det__allegato-info">
                        <div className="bop-finanz-det__allegato-name">{a.nomeFile}</div>
                        <div className="bop-finanz-det__allegato-meta">
                          {fmtBytes(a.dimensioneBytes)} · {fmtData(a.createdAt)} · {a.uploadedBy.nome}
                        </div>
                      </div>
                      <div className="bop-finanz-det__allegato-actions">
                        <button
                          className="bop-btn-secondary"
                          // @api GET /api/finanziamenti/:id/allegati/:allegatoId/download -> file stream (attachment)
                          onClick={() => apiDownload(`/api/finanziamenti/${id}/allegati/${a.id}/download`, a.nomeFile)}
                        >
                          Scarica
                        </button>
                        {can('amministrazione.finanziamenti', 'delete') && (
                          <button
                            className="bop-btn-icon bop-text-danger"
                            title="Elimina allegato"
                            onClick={() => handleDeleteAllegato(a.id)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Modale: contabilizza rata (nuovo movimento finanziamento) ── */}
      {contabOpen && (
        <Modal
          title="Nuovo movimento finanziamento"
          onClose={() => setContabOpen(false)}
          size="lg"
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={() => setContabOpen(false)} disabled={contabSaving}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaContabilizzazione} disabled={contabSaving}>
                {contabSaving ? 'Salvataggio…' : 'Crea movimento'}
              </button>
            </>
          }
        >
          <FormMovimento
            form={contabForm}
            setForm={setContabForm}
            finanziamentiLista={contabFinList}
            rateDisponibili={contabRate}
            disabledFinanziamento={true}
            onChangeFinanziamento={() => {}}
            onTouchPeriodo={() => setContabManuPeriodo(true)}
            ricalcolaPeriodo={ricalcolaPeriodoContab}
            error={contabError}
          />
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
